import { action, makeObservable, observable } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { logger } from "@/modules/logger";

/**
 * Store responsible for tracking analytics events state.
 *
 * This store keeps track of which analytics events have been fired during
 * the session to prevent duplicate events, especially across component
 * mount/unmount cycles.
 */
export class AppStoreMetricsStore extends AppSubStore {
  /**
   * Tracks whether the app_store_initialize event has been fired.
   * This ensures the event only fires once per session.
   */
  hasTrackedAppStoreInitialize = false;

  /**
   * Tracks whether the ui_ready event has been fired.
   * This ensures the event only fires once per session, even if components remount.
   */
  hasTrackedUiReady = false;

  /**
   * Tracks whether the welcome_overlay_view event has been fired.
   * This ensures the event only fires once per browser session.
   */
  hasTrackedWelcomeOverlayView = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable(this, {
      hasTrackedAppStoreInitialize: observable,
      hasTrackedUiReady: observable,
      hasTrackedWelcomeOverlayView: observable,
      trackAppStoreInitialize: action,
      trackUiReady: action,
      trackWelcomeOverlayView: action,
    });
  }

  /**
   * Tracks the app_store_initialize event exactly once per session.
   *
   * This should be called when the AppStore.initialize() method completes successfully:
   * - Account info has been fetched
   * - Spaces and SpaceAccounts have been loaded
   * - Platform info has been initialized
   * - User has been identified in metrics
   * - Observability has been set up
   * - Local and MemDB databases have been initialized
   */
  trackAppStoreInitialize = () => {
    if (this.hasTrackedAppStoreInitialize) {
      return;
    }

    logger.info({
      message: "[AppStoreMetricsStore]: Tracking app_store_initialize event",
    });

    trackEvent(TrackedEvent.AppStoreInitialize);
    this.hasTrackedAppStoreInitialize = true;
  };

  /**
   * Tracks the ui_ready event exactly once per session.
   *
   * This should be called when the app becomes fully interactive:
   * - User authentication is complete
   * - App store initialization is done (user data, observability, databases)
   * - Bootstrap process is complete (all user data downloaded and processed)
   * - Sync system is activated (action queue, polling, Pusher)
   * - UI is ready for user interaction
   */
  trackUiReady = ({ isReady }: { isReady?: boolean }) => {
    if (!isReady || this.hasTrackedUiReady) {
      return;
    }

    logger.info({
      message: "[AppStoreMetricsStore]: Tracking ui_ready event",
    });

    trackEvent(TrackedEvent.UiReady);
    this.hasTrackedUiReady = true;
  };

  /**
   * Tracks the welcome_overlay_view event exactly once per browser session.
   *
   * This should be called when the welcome overlay is shown for the first time
   * in the session.
   */
  trackWelcomeOverlayView = (properties?: Record<string, unknown>) => {
    if (this.hasTrackedWelcomeOverlayView) {
      return;
    }

    logger.info({
      message: "[AppStoreMetricsStore]: Tracking welcome_overlay_view event",
    });

    trackEvent(TrackedEvent.WelcomeOverlayView, properties);
    this.hasTrackedWelcomeOverlayView = true;
  };
}
